/**
 * Bridge for the native BroadcastAutoStart plugin.
 *
 * Provides a one-tap entry into screen capture and surfaces the frames the
 * native side produces:
 *   - iOS: the ReplayKit Broadcast Upload Extension, writing frames to the
 *     App Group container.
 *   - Android: the MediaProjection foreground service (ScreenCaptureService),
 *     streaming JPEG `frame` events straight to JS (same process).
 *
 * Available on iOS 12+ (with the BUE target) and Android (minSdk 24). On web
 * the plugin reports unavailable and start/stop are no-ops.
 */
import { registerPlugin, type PluginListenerHandle } from "@capacitor/core";
import { getPlatform } from "./platform";
import { getViewScale } from "./viewScale";

export interface BroadcastFrame {
  dataUrl: string;
  bytes: number;
  ts: number;
}

/** `reason: "capture-ended"` = iOS stopped capturing the screen without the
 * extension reporting it (the extension was terminated); absent = the
 * extension's own finished notification. */
export interface BroadcastFinishedEvent {
  reason?: string;
}

interface BroadcastAutoStartPlugin {
  isAvailable(): Promise<{ available: boolean }>;
  startBroadcast(): Promise<{ triggered: boolean }>;
  stopBroadcast(): Promise<void>;
  armStopPicker(rect: { x: number; y: number; width: number; height: number }): Promise<{ armed: boolean }>;
  disarmStopPicker(): Promise<void>;
  requestFrame(): Promise<void>;
  openPokemonGo(): Promise<{ opened: boolean; reason?: string; error?: string }>;
  addListener(event: "started", cb: () => void): Promise<PluginListenerHandle>;
  addListener(event: "frame", cb: (frame: BroadcastFrame) => void): Promise<PluginListenerHandle>;
  addListener(event: "finished", cb: (data?: BroadcastFinishedEvent) => void): Promise<PluginListenerHandle>;
  addListener(event: "log", cb: (data: { line: string }) => void): Promise<PluginListenerHandle>;
}

const BroadcastAutoStart = registerPlugin<BroadcastAutoStartPlugin>("BroadcastAutoStart");

/**
 * Native screen capture is available on iOS (ReplayKit) and Android
 * (MediaProjection); only web has no capture path. Let the native
 * `isAvailable()` make the final call on the supported platforms.
 */
function isBroadcastPlatform(): boolean {
  const p = getPlatform();
  return p === "ios" || p === "android";
}

export async function canAutoStartBroadcast(): Promise<boolean> {
  if (!isBroadcastPlatform()) {
    // eslint-disable-next-line no-console
    console.log("[live-scan] BroadcastAutoStart unavailable, platform=", getPlatform());
    return false;
  }
  try {
    const { available } = await BroadcastAutoStart.isAvailable();
    // eslint-disable-next-line no-console
    console.log("[live-scan] BroadcastAutoStart.isAvailable =", available);
    return !!available;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[live-scan] BroadcastAutoStart.isAvailable threw", err);
    return false;
  }
}

export async function startBroadcast(): Promise<{ triggered: boolean }> {
  if (!isBroadcastPlatform()) return { triggered: false };
  // eslint-disable-next-line no-console
  console.log("[live-scan] BroadcastAutoStart.startBroadcast() invoked");
  const res = await BroadcastAutoStart.startBroadcast();
  // eslint-disable-next-line no-console
  console.log("[live-scan] BroadcastAutoStart.startBroadcast →", res);
  return res;
}

export async function stopBroadcast(): Promise<void> {
  if (!isBroadcastPlatform()) return;
  await BroadcastAutoStart.stopBroadcast();
}

/**
 * iOS 26.7: only a REAL finger tap on the system picker presents "Stop
 * Broadcast" (a programmatic tap presents "Start" over the live broadcast).
 * While a scan runs, the menu lays the picker invisibly over its Stop & save
 * button (CSS px, viewport-relative) so the trainer's tap lands on it and the
 * system's own Stop sheet ends the broadcast; `finished` then closes the scan.
 * No-op off iOS.
 */
export async function armStopPicker(rect: { x: number; y: number; width: number; height: number }): Promise<boolean> {
  if (getPlatform() !== "ios") return false;
  try {
    // The native side lays the picker in web-view POINTS. The rect arrives
    // in layout CSS px, which equal points except on a Display Zoom phone,
    // where the page is laid out at 390px and scaled down (viewScale.ts).
    const s = getViewScale();
    const r = await BroadcastAutoStart.armStopPicker({
      x: rect.x * s,
      y: rect.y * s,
      width: rect.width * s,
      height: rect.height * s,
    });
    return !!r?.armed;
  } catch {
    return false;
  }
}

export async function disarmStopPicker(): Promise<void> {
  if (getPlatform() !== "ios") return;
  try {
    await BroadcastAutoStart.disarmStopPicker();
  } catch {
    /* pre-sync build */
  }
}

/**
 * Backpressure ack (Android). Tell the native capture service we have finished
 * the current frame and are ready for the next. MUST be called exactly once per
 * received `frame` event (even for frames the JS pipeline drops early), or the
 * demand-driven handshake deadlocks and no further frames arrive. No-op / safely
 * ignored on iOS and web, where there is no event-driven capture to pace.
 */
export async function requestNextFrame(): Promise<void> {
  // Android-only: iOS/web frames come from the App Group poller (pull-based),
  // which is already demand-driven, so there is nothing to ack there. Gating on
  // the platform (rather than catching a reject) avoids a wasted bridge hop per
  // frame on iOS.
  if (getPlatform() !== "android") return;
  try {
    await BroadcastAutoStart.requestFrame();
  } catch {
    /* plugin missing (e.g. pre-sync dev build): ignore. */
  }
}


/**
 * Android: bring Pokémon GO to the front through its launch intent (the
 * `intent://` URL navigation never worked under Capacitor's URL handler). Null
 * when the native method is unavailable (web, iOS, pre-sync build) so the
 * caller can fall back to the URL hand-off.
 */
export async function openPokemonGoNative(): Promise<{ opened: boolean; reason?: string } | null> {
  if (getPlatform() !== "android") return null;
  try {
    return await BroadcastAutoStart.openPokemonGo();
  } catch {
    return null;
  }
}

export function onFrame(cb: (frame: BroadcastFrame) => void): Promise<PluginListenerHandle> {
  return BroadcastAutoStart.addListener("frame", cb);
}

export function onFinished(cb: (data?: BroadcastFinishedEvent) => void): Promise<PluginListenerHandle> {
  return BroadcastAutoStart.addListener("finished", cb);
}

/** Native picker diagnostics, mirrored into the JS console so they reach the
 * exported scan log ("[broadcast-native] tapBroadcastPicker ..."). */
export function onNativeLog(cb: (line: string) => void): Promise<PluginListenerHandle> {
  return BroadcastAutoStart.addListener("log", (data) => cb(data?.line ?? ""));
}
